import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  ChannelMessage,
  ChannelRoom,
  channelHeartbeat,
  joinChannelRoom,
  leaveChannelRoom,
  sendChannelMessage,
  subscribeChannelRoom,
} from '../services/channelChat';
import { IconBolt } from './icons';
import './WatchParty.css';
import './ChannelChat.css';

const NICKNAME_KEY = 'pogchamp_chat_nickname';
const MESSAGE_CAP = 200;

const cleanNickname = (value: string): string =>
  value.trim().replace(/[^\w\s\-.]/g, '').slice(0, 20);

const savedNickname = (): string => {
  try {
    return localStorage.getItem(NICKNAME_KEY) || '';
  } catch {
    return '';
  }
};

interface ChannelChatProps {
  /** Room id on the server — the channel id ('rec', 'top', …) */
  room: string;
  /** Channel label — "Rec" */
  label: string;
  /** Twitch display name once logged in; it becomes the fixed nickname */
  preferredNickname?: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/**
 * The channel's shared room, docked under Twitch chat like a party's chat.
 * Everyone tuned to the channel is in here while each player follows the
 * heat on its own. Listening is free; pick a nickname to talk.
 */
export const ChannelChat: React.FC<ChannelChatProps> = ({
  room,
  label,
  preferredNickname,
  collapsed,
  onToggleCollapsed,
}) => {
  const [state, setState] = useState<ChannelRoom | null>(null);
  const [connected, setConnected] = useState(false);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [nickname, setNickname] = useState(() => preferredNickname || savedNickname());
  const [nickDraft, setNickDraft] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinned, setPinned] = useState(true);
  const [unread, setUnread] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);
  const roomRef = useRef(room);
  const nicknameRef = useRef(nickname);
  const participantRef = useRef<string | null>(null);
  const pinnedRef = useRef(true);
  const collapsedRef = useRef(collapsed);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { roomRef.current = room; }, [room]);
  useEffect(() => { nicknameRef.current = nickname; }, [nickname]);
  useEffect(() => { collapsedRef.current = collapsed; }, [collapsed]);

  // Give up the speaking slot (server-side identity) — the next message
  // simply joins again under the current nickname
  const dropParticipant = useCallback(() => {
    const pid = participantRef.current;
    if (pid) leaveChannelRoom(roomRef.current, pid);
    participantRef.current = null;
    setParticipantId(null);
  }, []);

  // Twitch identity wins as soon as OAuth finishes loading
  useEffect(() => {
    if (!preferredNickname || preferredNickname === nicknameRef.current) return;
    dropParticipant();
    setNickname(preferredNickname);
  }, [preferredNickname, dropParticipant]);

  // One live feed per room. Every (re)connect opens with a full state event,
  // so a dropped SSE simply resyncs when it comes back.
  useEffect(() => {
    setState(null);
    setConnected(false);
    setError(null);
    setUnread(0);
    pinnedRef.current = true;
    setPinned(true);

    const unsubscribe = subscribeChannelRoom(room, {
      onState: next => {
        setState(next);
        setConnected(true);
      },
      onPresence: presence => {
        setState(prev => (prev ? { ...prev, ...presence } : prev));
      },
      onMessage: (message: ChannelMessage) => {
        setState(prev => {
          if (!prev || prev.messages.some(m => m.id === message.id)) return prev;
          const messages = [...prev.messages, message];
          if (messages.length > MESSAGE_CAP) messages.splice(0, messages.length - MESSAGE_CAP);
          return { ...prev, messages };
        });
        if (collapsedRef.current || !pinnedRef.current) setUnread(n => n + 1);
      },
      onError: () => setConnected(false),
    });

    return () => {
      unsubscribe();
      const pid = participantRef.current;
      if (pid) leaveChannelRoom(room, pid);
      participantRef.current = null;
      setParticipantId(null);
    };
  }, [room]);

  // Keep the speaking slot alive; if the server forgot us, rejoin lazily
  useEffect(() => {
    if (!participantId) return;
    const timer = setInterval(() => {
      channelHeartbeat(room, participantId).catch((err: any) => {
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
          participantRef.current = null;
          setParticipantId(null);
        }
      });
    }, 20000);
    return () => clearInterval(timer);
  }, [room, participantId]);

  // Follow the live edge unless the reader scrolled up on purpose
  const messageCount = state?.messages.length ?? 0;
  useEffect(() => {
    const list = listRef.current;
    if (list && pinnedRef.current) list.scrollTop = list.scrollHeight;
  }, [messageCount, collapsed]);

  useEffect(() => () => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
  }, []);

  const flashError = (message: string) => {
    setError(message);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(null), 2500);
  };

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    const nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 48;
    pinnedRef.current = nearBottom;
    setPinned(nearBottom);
    if (nearBottom) setUnread(0);
  };

  const jumpToLive = () => {
    pinnedRef.current = true;
    setPinned(true);
    setUnread(0);
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  };

  const expand = () => {
    setUnread(0);
    pinnedRef.current = true;
    setPinned(true);
    onToggleCollapsed();
  };

  const ensureJoined = async (): Promise<string> => {
    if (participantRef.current) return participantRef.current;
    const result = await joinChannelRoom(room, nicknameRef.current);
    participantRef.current = result.participantId;
    setParticipantId(result.participantId);
    return result.participantId;
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (!value || sending || !nickname) return;
    setSending(true);
    try {
      const pid = await ensureJoined();
      await sendChannelMessage(room, pid, value);
      setText('');
      jumpToLive();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 429) {
        flashError('Slow down a little.');
      } else if (status === 403) {
        // Pruned while idle — next send rejoins
        participantRef.current = null;
        setParticipantId(null);
        flashError('Reconnected — send that again.');
      } else {
        flashError(err?.response?.data?.error || 'Message failed to send.');
      }
    } finally {
      setSending(false);
    }
  };

  const saveNickname = (event: FormEvent) => {
    event.preventDefault();
    const next = cleanNickname(nickDraft);
    if (!next) return;
    try { localStorage.setItem(NICKNAME_KEY, next); } catch {}
    setNickname(next);
    setNickDraft('');
  };

  const changeNickname = () => {
    dropParticipant();
    setNickDraft(nickname);
    setNickname('');
    try { localStorage.removeItem(NICKNAME_KEY); } catch {}
  };

  const online = state?.online ?? 0;
  const presence = (
    <span
      className={`channel-live ${connected ? 'on' : ''}`}
      title={connected ? `${online} tuned to ${label} right now` : 'Reconnecting to channel chat…'}
    >
      <span className="channel-live-dot" aria-hidden />
      {connected ? `${online} here` : 'offline'}
    </span>
  );

  if (collapsed) {
    return (
      <button type="button" className="channel-chat collapsed" onClick={expand} title="Show channel chat">
        <span className="watch-party-title">
          <IconBolt size={13} className="channel-chat-icon" />
          <span>{label} chat</span>
          {presence}
        </span>
        <span className="channel-chat-expand">
          {unread > 0 ? `${unread} new · ` : ''}show
        </span>
      </button>
    );
  }

  return (
    <div className="channel-chat">
      <div className="watch-party-header">
        <div className="watch-party-title">
          <IconBolt size={14} className="channel-chat-icon" />
          <span>{label} chat</span>
          {presence}
        </div>
        <div className="watch-party-header-actions">
          <button className="watch-party-close" onClick={onToggleCollapsed} title="Hide channel chat" aria-label="Hide channel chat">×</button>
        </div>
      </div>

      <div className="watch-party-messages channel-chat-messages" ref={listRef} onScroll={handleScroll}>
        {!state ? (
          <div className="watch-party-empty">{connected ? 'Loading…' : 'Connecting…'}</div>
        ) : state.messages.length === 0 ? (
          <div className="watch-party-empty">
            Quiet in here — say something.
            <br />
            Everyone tuned to {label} shares this room while their player follows the heat.
          </div>
        ) : (
          state.messages.map(m => (
            <div key={m.id} className={`party-msg ${m.participantId === participantId ? 'own' : ''}`}>
              <span className="party-msg-nick">{m.nickname}:</span>
              <span className="party-msg-text">{m.text}</span>
            </div>
          ))
        )}
      </div>

      {!pinned && unread > 0 && (
        <button type="button" className="channel-chat-jump" onClick={jumpToLive}>
          ↓ {unread} new
        </button>
      )}

      {error && <div className="channel-chat-error" role="status">{error}</div>}

      {nickname ? (
        <form className="watch-party-input" onSubmit={send}>
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={`Message ${label} chat…`}
            maxLength={400}
            disabled={sending}
          />
          <button type="submit" disabled={!text.trim() || sending}>Send</button>
        </form>
      ) : (
        <form className="watch-party-input channel-chat-join" onSubmit={saveNickname}>
          <input
            type="text"
            value={nickDraft}
            onChange={e => setNickDraft(e.target.value)}
            placeholder="Pick a nickname to talk"
            maxLength={20}
            autoComplete="nickname"
          />
          <button type="submit" disabled={!cleanNickname(nickDraft)}>Join</button>
        </form>
      )}

      {nickname && (
        <div className="channel-chat-foot">
          <span>
            chatting as <strong>{nickname}</strong>
            {preferredNickname ? ' · Twitch' : ''}
          </span>
          {!preferredNickname && (
            <button type="button" className="channel-chat-rename" onClick={changeNickname}>change</button>
          )}
        </div>
      )}
    </div>
  );
};
